import catalogueData from './catalogue.zh-TW.json';

interface DocumentTranslation {
  title: string;
  description?: string;
  sourceTitle: string;
  descriptionFingerprint?: string;
}

const catalogue = catalogueData as {
  documents: Record<string, DocumentTranslation>;
  terms: Record<string, string>;
};

export function isTraditionalChinese(language: string): boolean {
  return /^zh(?:-|$)/i.test(language);
}

/** A version marker for display translations, not a security checksum. */
export function contentFingerprint(value: string): string {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash = Math.imul(hash ^ value.charCodeAt(index), 16777619);
  }
  return `${value.length}:${(hash >>> 0).toString(16)}`;
}

export function localizeText(value: string, language: string): string {
  return isTraditionalChinese(language) ? catalogue.terms[value] ?? value : value;
}

export function localizeDocumentTitle(doc: { id: string; title: string }, language: string): string {
  const translation = catalogue.documents[doc.id];
  return isTraditionalChinese(language) && translation?.sourceTitle === doc.title
    ? translation.title
    : doc.title;
}

export function localizeDocumentDescription(id: string, source: string, language: string): string {
  const translation = catalogue.documents[id];
  return isTraditionalChinese(language) && translation?.descriptionFingerprint === contentFingerprint(source)
    ? translation.description ?? source
    : source;
}

/** Keep source objects and identifiers intact; translate only known display values. */
export function localizeData<T>(source: T, language: string): T {
  if (!isTraditionalChinese(language)) return source;
  if (typeof source === 'string') return localizeText(source, language) as T;
  if (Array.isArray(source)) return source.map(item => localizeData(item, language)) as T;
  if (source && typeof source === 'object') {
    const result = Object.fromEntries(Object.entries(source).map(([key, value]) => [key, localizeData(value, language)]));
    return result as T;
  }
  return source;
}

const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

/** Parse archive dates explicitly: historical two-digit years use the record's year. */
export function localizeArchiveDate(source: string, language: string, yearHint?: number | null): string {
  if (!isTraditionalChinese(language)) return source;
  const numeric = source.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
  if (numeric) {
    const month = Number(numeric[1]);
    const day = Number(numeric[2]);
    const rawYear = Number(numeric[3]);
    const year = rawYear < 100
      ? yearHint && yearHint % 100 === rawYear ? yearHint : rawYear > 30 ? 1900 + rawYear : 2000 + rawYear
      : rawYear;
    const date = new Date(Date.UTC(year, month - 1, day));
    if (date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day) {
      return `${year} 年 ${month} 月 ${day} 日`;
    }
  }
  const monthYear = source.match(/^([A-Za-z]+),?\s+(\d{4})$/);
  if (monthYear) {
    const month = MONTHS.indexOf(monthYear[1].toLowerCase()) + 1;
    if (month) return `${monthYear[2]} 年 ${month} 月`;
  }
  return localizeText(source, language);
}
